import React, { useRef, useEffect } from 'react'
import EChartsReact from 'echarts-for-react'
import { PLOTS_COLORS } from './Colors'

/**
 * Silhouette Plot.
 * This component handles data processing and chart configuration.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.silhouetteData - The silhouette plot data loaded lazily.
 * @param {Array<number>} props.silhouetteData.silhouette_samples - Silhouette scores per respondent.
 * @param {Array<number>} props.silhouetteData.clusters_assigned - Segment assignments per respondent.
 * @param {boolean} props.isLoading - Whether the data is currently loading.
 * @param {React.RefObject} props.containerRef - Ref to the container element for resize observation.
 * @returns {JSX.Element} The rendered ECharts component.
 */
export function SilhouettePlotChart({ silhouetteData, isLoading, containerRef }) {
  const chartRef = useRef(null)

  // Use ResizeObserver to handle flex layout timing issues
  useEffect(() => {
    if (!containerRef?.current || !chartRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      const chartInstance = chartRef.current?.getEchartsInstance()
      if (chartInstance) {
        chartInstance.resize()
      }
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [containerRef])


  let segmentAssignments = []
  let silhouetteSamples = []
  let avgSilhouetteScore = 0
  let silhouetteDataBySegment = {}

  if (silhouetteData && silhouetteData.clusters_assigned && silhouetteData.silhouette_samples) {
    segmentAssignments = silhouetteData.clusters_assigned
    silhouetteSamples = silhouetteData.silhouette_samples

    // Calculate average silhouette score directly from raw data
    avgSilhouetteScore =
      silhouetteSamples.length > 0
        ? silhouetteSamples.reduce((acc, score) => acc + score, 0) /
          silhouetteSamples.length
        : 0

    // Group silhouette scores by segment for visualization
    segmentAssignments.forEach((segmentLabel, index) => {
      const silhouetteValue = silhouetteSamples[index]

      if (!silhouetteDataBySegment[segmentLabel]) {
        silhouetteDataBySegment[segmentLabel] = []
      }
      silhouetteDataBySegment[segmentLabel].push(
        parseFloat(silhouetteValue.toFixed(3))
      )
    })

    // Sort silhouette scores within each segment
    Object.keys(silhouetteDataBySegment).forEach(segmentLabel => {
      silhouetteDataBySegment[segmentLabel] = silhouetteDataBySegment[segmentLabel].sort(
        (a, b) => a - b
      )
    })
  }

  const segments = Object.keys(silhouetteDataBySegment).map(Number)

  /**
   * Function to assign colors to segments
   * @param {number|string} segment - segment label
   * @returns {string} - Color code
   */
  const getColorBySegment = segment => {
    const COLORS = PLOTS_COLORS
    // Map segment labels to indices to ensure consistent color assignment
    const segmentIndex = segments.indexOf(Number(segment))
    return COLORS[segmentIndex % COLORS.length]
  }

  /**
   * Function to generate the chart options for the Silhouette Plot
   * @returns {Object} - ECharts options object
   */
  const getSilhouetteChartOptions = () => {
    const data = []

    segments.forEach(segment => {
      const segmentData = silhouetteDataBySegment[segment]
      segmentData.forEach(value => {
        data.push({
          value: value,
          itemStyle: {
            color: getColorBySegment(segment),
          },
          name: `Segment ${segment}`,
        })
      })
    })

    return {
      tooltip: {
        show: true,
        trigger: 'item',
        formatter: params => {
          if (params.componentType === 'series') {
            return `${params.data.name}<br/>Silhouette: ${params.value}`
          }
          return null
        },
      },
      xAxis: {
        type: 'value',
        name: 'Silhouette \nScore',
        nameLocation: 'middle',
        nameGap: 30,
      },
      yAxis: {
        type: 'category',
        name: 'Respondents',
        axisLabel: {
          show: false, // Hide y-axis labels for better visualization
        },
        splitLine: {
          show: false, // Hide y-axis split lines
        },
        axisTick: {
          show: false, // Hide y-axis ticks
        },
      },
      toolbox: {
        show: true,
        feature: {
          dataView: { show: false, readOnly: false },
          saveAsImage: { show: true },
        },
      },
      series: [
        {
          name: 'Silhouette Scores',
          type: 'bar',
          data,
          barCategoryGap: '50%',
          label: {
            show: false,
          },
          markLine: {
            symbol: 'none',
            silent: true,
            data: [
              {
                name: 'Average Silhouette Score',
                xAxis: avgSilhouetteScore,
                lineStyle: {
                  color: 'red',
                  type: 'dashed',
                },
                label: {
                  formatter: `Avg. Score: ${avgSilhouetteScore.toFixed(3)}`,
                  position: 'end',
                  color: 'red',
                },
              },
            ],
          },
        },
      ],
    }
  }

  return (
    <EChartsReact
      ref={chartRef}
      option={getSilhouetteChartOptions()}
      style={{ height: '100%', width: '100%' }}
      showLoading={isLoading}
    />
  )
}
